import PropTypes from 'prop-types';
import React, { FC, useMemo } from 'react';

import t from '../../../labels/labelling-utils';
import { PREDICTEDLINEUPSLIM } from '../../../typings/report-tabs';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Cta from '../../atoms/cta/cta';
import IconChevronRight from '../../atoms/icons/icon-chevron-right';
import { IComponent } from '../../../typings/generic-component';
import Text from '../../atoms/text/text';
import ReportTabsPanel from '../../molecules/report-tabs-panel/report-tabs-panel';
import Team, { ITeam as ITeamTypings } from '../../molecules/team/team';
import TeamsTabs, { ITeamsTabs } from '../../molecules/teams-tabs/teams-tabs';
import MatchFormationField, {
  IFormationPlayer,
  IMatchFormationField,
} from '../../organisms/match-formation-field/match-formation-field';
import { isMobile } from '../../../utils/device-utils';
import { getUrlStatic } from '../../../utils/placeholders-football';

import './predicted-lineups-detail.scss';

export type ActiveTabType = 'home' | 'away';

export interface ITeamPredictedLineups extends ITeamTypings {
  playersInFormation: Array<IFormationPlayer>;
}

export interface IPredictedLineupsDetail extends IComponent {
  activeTab?: ActiveTabType;
  away: ITeamPredictedLineups;
  competitionId?: string; // BE sends this, but it's actually unused in DS (will be MANDATORY?)
  ctaHpUrl?: string | null; // FIXME: for going BACK to PREDICTED LINEUPS HP we will use THIS LINK or CTAURL inside HOME/AWAY teams?
  ctaLmpUrl?: string | null; // ISS-6463 - LMP PREDICTED LINEUPS changed in ISS-6846
  ctaLmpLineupUrl?: string | null; // ISS-6846 - LMP OFFICIAL LINEUPS
  date: string;
  hasImage?: boolean;
  home: ITeamPredictedLineups;
  onTabChange?: (type: ActiveTabType) => void;
  teamId?: string; // BE sends this, but it's actually unused in DS (will be MANDATORY?)
  predictedSponsorLink: string;
}

const classNames = (): IClassNames =>
  makeClassNames({
    root: 'predicted-lineups-detail',
    tabs: 'predicted-lineups-detail__tabs',
    formation: 'predicted-lineups-detail__formation',
    formationData: 'predicted-lineups-detail__formation-data',
    formationBadge: 'predicted-lineups-detail__formation-badge',
    matchDate: 'predicted-lineups-detail__match-date',
    cta: 'predicted-lineups-detail__cta',
    report: 'predicted-lineups-detail__report',
    dateLong: 'predicted-lineups-detail__date-long',
    dateShort: 'predicted-lineups-detail__date-short',
    adv: 'predicted-lineups-detail__adv',
    advDefault: 'predicted-lineups-detail__adv-default',
  });

const PredictedLineupsDetail: FC<IPredictedLineupsDetail> = ({
  away,
  competitionId, // FIXME: actually UNUSED
  ctaHpUrl,
  ctaLmpUrl,
  ctaLmpLineupUrl,
  date,
  home,
  teamId, // FIXME: actually UNUSED
  predictedSponsorLink,
  trackingData,
  /* istanbul ignore next: default value */ activeTab = 'home',
  /* istanbul ignore next: default value */ hasImage = false,
  /* istanbul ignore next: default value */ onTabChange = () => {},
}) => {
  const cn = classNames();

  const imageUrlDesktop = `https://${getUrlStatic()}/images/skysport/campagne/${competitionId}/predictedDesktop.png`;
  const imageUrlMobile = `https://${getUrlStatic()}/images/skysport/campagne/${competitionId}/predictedBanner.png`;

  const teamsTabsProps: ITeamsTabs = {
    idHome: `home-${home.id}`,
    idAway: `away-${away.id}`,
    home: { ...home, formation: undefined },
    away: { ...away, formation: undefined },
    hasInput: true,
    hasDesktopInput: true,
    roundLabel: t('match.statistics.label.vs'),
    onChange: onTabChange,
    initialValue: activeTab,
  };

  const activeTeam = activeTab === 'home' ? home : away;
  const { playersInFormation, ...team } = activeTeam;
  const hasValidBackUrl: boolean = Boolean(ctaHpUrl && home.seoName && away.seoName);
  const ctaBackUrl: string = hasValidBackUrl ? `${ctaHpUrl}#${home.seoName}-${away.seoName}` : '#';

  if (!activeTeam.formation) {
    throw new Error('Formation is required');
  }

  const formationProps: IMatchFormationField = {
    players: playersInFormation ?? [],
    formation: activeTeam.formation,
    hasImage,
  };

  const dateShort = dateFormat({ date, format: 'ddd D MMM YYYY' });
  const dateLong = dateFormat({ date, format: 'dddd D MMMM YYYY' });

  const callToAction = useMemo(() => {
    if (!ctaLmpUrl && !ctaLmpLineupUrl) {
      return (
        <div className={cn.cta}>
          <Cta
            href={ctaBackUrl}
            onClick={hasValidBackUrl ? undefined : () => history.back()} // WORKAROUND if NO VALID BACK info are provided
            Icon={IconChevronRight}
            iconPosition="right"
            iconSize="xxsmall"
            trackingData={trackingData}
          >
            {t('match.lineups.back')}
          </Cta>
        </div>
      );
    }

    let label: 'match.lineups.lmp.cta' | 'match.lineups.lmp.lineup-cta' = 'match.lineups.lmp.cta';
    let url: string = ctaLmpUrl ?? ctaBackUrl;

    if (ctaLmpLineupUrl) {
      label = 'match.lineups.lmp.lineup-cta';
      url = ctaLmpLineupUrl;
    }

    return (
      <div className={cn.cta}>
        <Cta href={url} Icon={IconChevronRight} iconPosition="right" iconSize="xxsmall" trackingData={trackingData}>
          {t(label)}
        </Cta>
      </div>
    );
  }, [ctaBackUrl, ctaLmpUrl, ctaLmpLineupUrl, cn, hasValidBackUrl, trackingData]);

  return (
    <div className={cn.root}>
      <div className={cn.tabs}>
        <TeamsTabs {...teamsTabsProps} />
      </div>
      <div className={cn.formation}>
        <MatchFormationField {...formationProps} />
        <div className={cn.formationData}>
          <div>
            <div className={cn.formationBadge}>{activeTeam.formation.split('').join(' - ')}</div>
          </div>
          <div className={cn.matchDate}>
            <Text color="light" fontSize="16" className={cn.dateLong}>
              {dateLong}
            </Text>
            <Text color="light" fontSize="16" className={cn.dateShort}>
              {dateShort}
            </Text>
            <Text color="light" fontSize="16" fontWeight="500">
              {`${t('match.times.hours')} ${dateFormat({ date, format: 'hh:mm' })}`}
            </Text>
          </div>
        </div>
      </div>
      <div className={predictedSponsorLink.length > 0 ? cn.adv : `${cn.adv} ${cn.advDefault}`}>
        {predictedSponsorLink.length > 0 && (
          <React.Fragment>
            <img src={isMobile() ? imageUrlMobile : imageUrlDesktop} />
            <a href={predictedSponsorLink} target="_blank" rel="noreferrer" />
          </React.Fragment>
        )}
      </div>
      {callToAction}
      <div className={cn.report}>{<ReportTabsPanel home={team} panelVariant={PREDICTEDLINEUPSLIM} />}</div>
    </div>
  );
};

if (process.env.NODE_ENV !== 'production') {
  const teamPropTypes = PropTypes.shape({
    ...Team.propTypes!,
    playersInFormation: MatchFormationField.propTypes!.players!,
  }).isRequired;
  PredictedLineupsDetail.propTypes = {
    activeTab: PropTypes.oneOf<ActiveTabType>(['away', 'home']),
    away: teamPropTypes,
    competitionId: PropTypes.string,
    ctaHpUrl: PropTypes.string,
    ctaLmpUrl: PropTypes.string,
    date: PropTypes.string.isRequired,
    hasImage: PropTypes.bool,
    home: teamPropTypes,
    onTabChange: PropTypes.func,
    teamId: PropTypes.string,
  };
}

export default PredictedLineupsDetail;
